import { useMemo } from "react"
import { type CombinedError, useQuery } from "urql"

import type { Query, TestTemplate, TestTemplateListSearchBy } from "@/graphql/codegen/graphql"
import { GetTestTemplateSuggestionsListDocument, SortingOrder, TestTemplateSortField } from "@/graphql/codegen/graphql"

interface UseGetTestTemplateSuggestionsProps {
  query: string
  productId: number
  companyId?: string | null
  locationId?: string | null
  pause?: boolean
  sort?: {
    field: TestTemplateSortField
    order: SortingOrder
  }
  limit?: number
  searchBy?: TestTemplateListSearchBy[]
}

interface UseGetTestTemplateSuggestionsReturn {
  testTemplates: TestTemplate[]
  fetching: boolean
  error?: CombinedError
}

export const useGetTestTemplateSuggestions = ({
  query,
  productId,
  companyId,
  locationId,
  sort = { field: TestTemplateSortField.CreatedOn, order: SortingOrder.Asc },
  pause = false,
  limit = 50,
  searchBy,
}: UseGetTestTemplateSuggestionsProps): UseGetTestTemplateSuggestionsReturn => {
  const [{ data, error, fetching }] = useQuery<Pick<Query, "testTemplate">>({
    query: GetTestTemplateSuggestionsListDocument,
    variables: {
      productId,
      companyId: companyId ?? null,
      locationId: locationId ?? null,
      input: {
        filter: {
          query,
          searchBy,
        },
        offset: 0,
        limit,
        sort,
      },
    },
    pause: pause || (query?.length ?? 0) < 2, // Conditionally pause the query
  })

  const testTemplates = useMemo(() => {
    return data?.testTemplate?.list?.__typename === "TestTemplateListOutputSuccess"
      ? data?.testTemplate.list.testTemplates
      : []
  }, [data])

  return {
    testTemplates,
    error,
    fetching,
  }
}
